/**
 * Game Constants
 * All fixed values and configuration constants for the game
 */

// Game version
export const VERSION = 'v1.00';

// Game dimensions (locked aspect ratio)
export const GAME_CONFIG = {
  WIDTH: 1920,
  HEIGHT: 1080,
  FPS: 60,
} as const;

// Player configuration
export const PLAYER_CONFIG = {
  SPEED: 650, // Max pixels per second
  ACCELERATION: 12000, // pixels/sec^2 - near-instant ramp (reaches max in ~0.05s)
  DECELERATION: 8000, // pixels/sec^2 - fast stop with tiny slide
  START_X: 960, // center of screen
  START_Y: 876, // near ground (moved down 40px total from 836)
  WIDTH: 300, // Hitbox/positioning native width (matches walk/shoot/etc native frames). Idle frames are 600×760 but rendered via setDisplaySize.
  HEIGHT: 380, // Hitbox/positioning native height
  SCALE: 0.85, // Hitbox/positioning scale. The on-screen sprite is sized via setDisplaySize(WIDTH × SCALE × 1.2, HEIGHT × SCALE × 1.2) so all native frame sizes (300×380 or 600×760) render identically at 306×388.
  // Dash configuration
  DASH_SPEED: 1800, // pixels per second during dash
  DASH_DURATION: 120, // milliseconds
  DASH_COOLDOWN: 800, // milliseconds between dashes
  DASH_DOUBLE_TAP_WINDOW: 250, // ms window for double-tap detection
  // Charged shot configuration
  CHARGE_TIME: 600, // ms to fully charge
  CHARGE_SPEED_MULTIPLIER: 1.6, // charged projectile grows faster
  CHARGE_WIDTH_MULTIPLIER: 2.0, // charged projectile is wider
} as const;

// Projectile configuration (Pang-style growing harpoon)
export const PROJECTILE_CONFIG = {
  WIDTH: 16, // Line width in pixels (legacy, not used for sprites)
  SPRITE_SIZE: 32, // Size of each energy projectile sprite
  SPRITE_SPACING: 8, // Spacing between stacked projectiles
  GROWTH_SPEED: 1100, // How fast projectiles spawn upward (pixels per second)
  HOLD_DURATION: 200, // How long to hold at max height (milliseconds) - 0.2 seconds
  MAX_LIFETIME: 400, // Maximum total lifetime (milliseconds) - safety timeout
  MAX_ACTIVE: 2, // Only one projectile at a time
} as const;

// Ball sizes and properties
export const BALL_SIZES = {
  LARGE: {
    radius: 128, // Doubled from 64
    points: 100,
    scale: 1.0,
  },
  MEDIUM: {
    radius: 64, // Doubled from 32
    points: 200,
    scale: 0.5,
  },
  SMALL: {
    radius: 32, // Doubled from 16
    points: 300,
    scale: 0.25,
  },
} as const;

// Physics constants
export const PHYSICS_CONFIG = {
  GRAVITY: 980, // pixels per second squared
  BOUNCE_DAMPENING: 0.8, // velocity reduction on bounce
  MIN_BOUNCE_HEIGHT: 240, // Minimum Y position for balls (double player height: 380×2=760px from ground)
  GROUND_Y: 1000,
  CEILING_Y: 80,
  WALL_LEFT: 50,
  WALL_RIGHT: 1870,
} as const;

// Shared vertical gap (px) from the bottom of the POSEIDON CHALLENGE title logo
// to the top edge of a card/panel. Card screens compute panelTop from the
// title's rendered bottom + this gap so logo-to-card spacing is identical
// everywhere the HELMEPA + title header sits above a card.
export const HEADER_CARD_GAP = 24;

// UI positions and styling
export const UI_CONFIG = {
  SCORE_X: 100,
  SCORE_Y: 40,
  TIMER_X: 960,
  TIMER_Y: 40,
  STAGE_NAME_X: 1820,
  STAGE_NAME_Y: 40,
  FONT_SIZE: 32,
  FONT_FAMILY: 'Arial',
  TEXT_COLOR: '#FFFFFF',
  TEXT_STROKE: '#000000',
  TEXT_STROKE_WIDTH: 6,
} as const;

// Camera configuration
export const CAMERA_CONFIG = {
  FADE_IN_DURATION: 500, // Fade in duration in ms when scene starts
  SHAKE_DURATION_BALL: 150, // Duration of shake when ball is destroyed
  SHAKE_DURATION_DEATH: 600, // Duration of shake when player dies
  SHAKE_INTENSITY_BASE: 0.002, // Base shake intensity (multiplied by ball size) - reduced for subtlety
  SHAKE_INTENSITY_DEATH: 0.008, // Shake intensity for player death - reduced for subtlety
  FLASH_DURATION: 300, // Duration of flash effect
  FLASH_COLOR_DEATH: { r: 255, g: 0, b: 0 }, // Red flash for death
  ZOOM_IN_SCALE: 1.3, // Zoom in scale for death (30% zoom)
  ZOOM_IN_DURATION: 200, // Zoom in duration (ms)
  ZOOM_OUT_DURATION: 400, // Zoom out duration (ms)
  // AAA hit-freeze durations by ball size
  HIT_FREEZE_SMALL: 30, // ms freeze for small ball pop
  HIT_FREEZE_MEDIUM: 50, // ms freeze for medium ball pop
  HIT_FREEZE_LARGE: 80, // ms freeze for large ball pop
  HIT_FREEZE_BOSS: 150, // ms freeze for boss hit
  // Combo zoom pulse
  COMBO_ZOOM_SCALE: 1.02, // Subtle zoom on combo milestone
  COMBO_ZOOM_DURATION: 150, // ms
} as const;

// Hit freeze duration based on ball radius
export function getHitFreezeDuration(radius: number): number {
  if (radius >= 100) return CAMERA_CONFIG.HIT_FREEZE_LARGE;
  if (radius >= 50) return CAMERA_CONFIG.HIT_FREEZE_MEDIUM;
  return CAMERA_CONFIG.HIT_FREEZE_SMALL;
}

// AAA Effects configuration
export const EFFECTS_CONFIG = {
  // Water caustics
  CAUSTICS_ALPHA: 0.08,
  CAUSTICS_SPEED: 0.0008, // animation speed
  // Screen edge glow for urgency
  URGENCY_GLOW_COLOR: 0xFF0000,
  URGENCY_GLOW_ALPHA: 0.3,
  // Low health vignette
  LOW_HEALTH_VIGNETTE_ALPHA: 0.4,
  LOW_HEALTH_PULSE_SPEED: 800, // ms per heartbeat
  // Chromatic aberration on big hits
  CHROMATIC_ABERRATION_STRENGTH: 4,
  CHROMATIC_ABERRATION_DURATION: 200,
  // Dash afterimage
  DASH_AFTERIMAGE_COUNT: 4,
  DASH_AFTERIMAGE_ALPHA: 0.4,
  DASH_AFTERIMAGE_FADE: 150, // ms
} as const;

// Difficulty presets
export enum Difficulty {
  STORY = 'STORY',
  EASY = 'EASY',
  NORMAL = 'NORMAL',
  HARD = 'HARD',
  MYTHIC = 'MYTHIC',
}

export const DIFFICULTY_CONFIG: Record<Difficulty, {
  lives: number;
  ballSpeedMultiplier: number;
  timerMultiplier: number;
  scoreMultiplier: number;
  powerupFrequency: number;
  description: string;
}> = {
  [Difficulty.STORY]: {
    lives: 99, ballSpeedMultiplier: 0.7, timerMultiplier: 2.0,
    scoreMultiplier: 0.5, powerupFrequency: 2.0,
    description: 'Enjoy the story - practically invincible',
  },
  [Difficulty.EASY]: {
    lives: 5, ballSpeedMultiplier: 0.85, timerMultiplier: 1.5,
    scoreMultiplier: 0.75, powerupFrequency: 1.5,
    description: 'Relaxed challenge with extra lives',
  },
  [Difficulty.NORMAL]: {
    lives: 3, ballSpeedMultiplier: 1.0, timerMultiplier: 1.0,
    scoreMultiplier: 1.0, powerupFrequency: 1.0,
    description: 'The intended experience',
  },
  [Difficulty.HARD]: {
    lives: 2, ballSpeedMultiplier: 1.3, timerMultiplier: 0.75,
    scoreMultiplier: 1.5, powerupFrequency: 0.5,
    description: 'For skilled warriors only',
  },
  [Difficulty.MYTHIC]: {
    lives: 1, ballSpeedMultiplier: 1.6, timerMultiplier: 0.5,
    scoreMultiplier: 3.0, powerupFrequency: 0.25,
    description: 'One life. No mercy. Legendary rewards.',
  },
};

// Current difficulty state
let globalDifficulty: Difficulty = Difficulty.NORMAL;
export const difficultyState = {
  getDifficulty: (): Difficulty => globalDifficulty,
  setDifficulty: (d: Difficulty): void => {
    globalDifficulty = d;
    try { localStorage.setItem('difficulty', d); } catch {}
  },
  getConfig: () => DIFFICULTY_CONFIG[globalDifficulty],
  loadFromStorage: (): void => {
    try {
      const stored = localStorage.getItem('difficulty');
      if (stored && stored in DIFFICULTY_CONFIG) globalDifficulty = stored as Difficulty;
    } catch {}
  },
};

// Debug configuration
export const DEBUG_CONFIG = {
  SHOW_HITBOXES: false, // Default state for debug mode
  HITBOX_COLOR: 0x00FF00, // Green color for hitboxes
  TEXT_COLOR: '#FFFFFF', // White text for debug info
  TEXT_SIZE: 12,
  TEXT_OFFSET: 5, // Offset from hitbox edge
} as const;

// Global debug state (shared between scenes)
let globalDebugMode: boolean = DEBUG_CONFIG.SHOW_HITBOXES;

export const debugState = {
  getDebugMode: (): boolean => globalDebugMode,
  setDebugMode: (enabled: boolean): void => { globalDebugMode = enabled; },
};

// Global design mode state (shared between scenes)
let globalDesignMode: boolean = false;

export const designModeState = {
  isDesignMode: (): boolean => globalDesignMode,
  setDesignMode: (enabled: boolean): void => { 
    globalDesignMode = enabled;
    console.log(`🎨 Design Mode: ${enabled ? 'ENABLED' : 'DISABLED'}`);
  },
};

// Global character only mode state (shared between scenes)
let globalCharacterOnlyMode: boolean = false;

export const characterOnlyModeState = {
  isCharacterOnlyMode: (): boolean => globalCharacterOnlyMode,
  setCharacterOnlyMode: (enabled: boolean): void => { globalCharacterOnlyMode = enabled; },
};

// Game states
export enum GameState {
  BOOT = 'BOOT',
  MENU = 'MENU',
  PLAYING = 'PLAYING',
  PAUSED = 'PAUSED',
  GAME_OVER = 'GAME_OVER',
  STAGE_COMPLETE = 'STAGE_COMPLETE',
}

// Player states
export enum PlayerState {
  IDLE = 'IDLE',
  RUNNING = 'RUNNING',
  SHOOTING = 'SHOOTING',
}

// Ball size types
export enum BallSize {
  LARGE = 'LARGE',
  MEDIUM = 'MEDIUM',
  SMALL = 'SMALL',
}

// Player lives
export const LIVES_CONFIG = {
  MAX_LIVES: 3,
  INVINCIBILITY_DURATION: 2000, // ms of invincibility after being hit
  FLASH_INTERVAL: 100, // ms between flashes during invincibility
} as const;

// Energy per life — Triple-AAA rework.
// Each life is a full "energy" bar. Hits drain energy; depleting the bar
// costs one life (continue) and refills the energy for a retry.
export const ENERGY_CONFIG = {
  MAX_ENERGY: 5,           // segments per life
  DAMAGE_PER_HIT: 2,       // normal bubble collision
  // Boss named attacks (sweep, lunge, dive). Softened from 3 to 1 for the
  // exhibition build so the hydra can never one-tap a player off a single
  // graze. With MAX_ENERGY 5 it now takes 5 boss-attack grazes to lose a
  // life (or a mix of 2-damage bubble hits + 1-damage attacks). Boss
  // bubbles still call playerHit() with default damage=1 so the curve
  // there is unchanged.
  DAMAGE_BOSS_ATTACK: 1,
  REGEN_PER_SECOND: 0,     // no auto-regen by default (tune later)
  LOW_ENERGY_THRESHOLD: 2, // UI switches to danger state at <= this
} as const;

// Demo mode
export const DEMO_CONFIG = {
  IDLE_TIMEOUT: 60000, // 60 seconds before demo starts
  DEMO_STAGE: 1, // Which stage to play in demo
} as const;

// Screen shake preference
let globalScreenShake: boolean = true;
export const screenShakeState = {
  isEnabled: (): boolean => globalScreenShake,
  setEnabled: (enabled: boolean): void => {
    globalScreenShake = enabled;
    try { localStorage.setItem('screenShake', String(enabled)); } catch {}
  },
  loadFromStorage: (): void => {
    try {
      const stored = localStorage.getItem('screenShake');
      if (stored !== null) globalScreenShake = stored === 'true';
    } catch {}
  },
};

// Feature flags — flip to re-enable hidden features
export const FEATURE_FLAGS = {
  ENDLESS_MODE: false,   // ENDLESS DEPTHS button on main menu
  TUTORIAL: false,       // TUTORIAL button on bottom bar
} as const;

// Scene keys
export const SCENE_KEYS = {
  BOOT: 'BootScene',
  MENU: 'MenuScene',
  NAME_ENTRY: 'NameEntryScene',
  GAME: 'GameScene',
  UI: 'UIScene',
  GAME_OVER: 'GameOverScene',
  STAGE_CLEAR: 'StageClearScene',
  LEADERBOARD: 'LeaderboardScene',
  BOSS: 'BossScene',
  TUTORIAL: 'TutorialScene',
  CREDITS: 'CreditsScene',
  SETTINGS: 'SettingsScene',
  CONTROLS: 'ControlsScene',
} as const;

// Asset keys
export const ASSET_KEYS = {
  // Sprites
  PLAYER_IDLE: 'player_idle',
  PLAYER_RUN: 'player_run',
  PLAYER_SHOOT: 'player_shoot',
  PROJECTILE: 'projectile',
  ENERGY_PROJECTILE: 'energyProjectile',
  BALL_LARGE: 'ball_large',
  BALL_MEDIUM: 'ball_medium',
  BALL_SMALL: 'ball_small',
  
  // Backgrounds
  BG_AEGEAN_SHORES: 'bg_aegean_shores',
  BG_OLYMPIAN_HEIGHTS: 'bg_olympian_heights',
  
  // Audio
  SFX_SHOOT: 'sfx_shoot',
  SFX_BALL_POP: 'sfx_ball_pop',
  SFX_BALL_SPLIT: 'sfx_ball_split',
  SFX_BOUNCE: 'sfx_bounce',
  SFX_GAME_OVER: 'sfx_game_over',
  SFX_STAGE_CLEAR: 'sfx_stage_clear',
  MUSIC_MENU: 'music_menu',
  MUSIC_GAMEPLAY: 'music_gameplay',
} as const;

// Input keys
export const INPUT_KEYS = {
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
  UP: 'UP',
  SPACE: 'SPACE',
  ESC: 'ESC',
  ENTER: 'ENTER',
} as const;

// Scoring
export const SCORE_CONFIG = {
  COMBO_TIMEOUT: 2000, // milliseconds before combo resets
  TIME_BONUS_THRESHOLD: 0.5, // 50% time remaining
  TIME_BONUS_POINTS: 500,
} as const;

// === AAA: Graphics Quality Presets ===
export enum GraphicsQuality {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  ULTRA = 'ULTRA',
}

export const GRAPHICS_CONFIG: Record<GraphicsQuality, {
  particles: boolean;
  particleMultiplier: number;
  caustics: boolean;
  postFX: boolean;
  ambientParticles: boolean;
  screenShake: boolean;
  glowEffects: boolean;
  description: string;
}> = {
  [GraphicsQuality.LOW]: {
    particles: true, particleMultiplier: 0.3, caustics: false,
    postFX: false, ambientParticles: false, screenShake: true, glowEffects: false,
    description: 'Best performance - minimal effects',
  },
  [GraphicsQuality.MEDIUM]: {
    particles: true, particleMultiplier: 0.6, caustics: false,
    postFX: true, ambientParticles: true, screenShake: true, glowEffects: true,
    description: 'Balanced quality and performance',
  },
  [GraphicsQuality.HIGH]: {
    particles: true, particleMultiplier: 1.0, caustics: true,
    postFX: true, ambientParticles: true, screenShake: true, glowEffects: true,
    description: 'Full visual quality',
  },
  [GraphicsQuality.ULTRA]: {
    particles: true, particleMultiplier: 1.5, caustics: true,
    postFX: true, ambientParticles: true, screenShake: true, glowEffects: true,
    description: 'Maximum effects - for powerful devices',
  },
};

let globalGraphicsQuality: GraphicsQuality = GraphicsQuality.HIGH;
export const graphicsState = {
  getQuality: (): GraphicsQuality => globalGraphicsQuality,
  setQuality: (q: GraphicsQuality): void => {
    globalGraphicsQuality = q;
    try { localStorage.setItem('graphicsQuality', q); } catch {}
  },
  getConfig: () => GRAPHICS_CONFIG[globalGraphicsQuality],
  loadFromStorage: (): void => {
    try {
      const stored = localStorage.getItem('graphicsQuality');
      if (stored && stored in GRAPHICS_CONFIG) globalGraphicsQuality = stored as GraphicsQuality;
    } catch {}
  },
};

// Animation frame rates
export const ANIMATION_CONFIG = {
  IDLE_FPS: 7, // Slow casual idle — 60 frames @ 7fps ≈ 8.6s/cycle. Source video is 10s; we play it back ~17% faster which stays well under the threshold where compression introduces jitter.
  RUN_FPS: 12,
  SHOOT_FPS: 16,
  BALL_FPS: 10,
} as const;
